/*
 * LeadsPage — org-wide inbox + per-project view.
 *
 * - No ?project=N  → org-wide inbox (all leads, project column, project filter dropdown)
 * - ?project=N     → per-project view (unchanged: detail panel, feedback, export)
 */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { api, type LeadResponse, type OrgLeadResponse, type ProjectResponse } from '@/lib/api'
import { buildProjectNumberMap, projectLabel, projectLabelById } from '@/lib/projectLabels'
import { HistoryScanControl } from '@/components/HistoryScanControl'

// ---------------------------------------------------------------------------
// Score confidence helpers
// ---------------------------------------------------------------------------

type Confidence = 'hi' | 'mid' | 'low' | 'na'
type LeadStatus = LeadResponse['lead_status']

const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'Новый',
  in_progress: 'Взят в работу',
  won: 'Конвертирован в заказ',
  lost: 'Отказ',
}

function LeadStatusText({ status }: { status: LeadStatus }) {
  if (status === 'won') {
    return (
      <>
        <span>Конвертирован</span>
        <span>в заказ</span>
      </>
    )
  }
  return <>{LEAD_STATUS_LABELS[status]}</>
}

function leadStatusPillClass(status: LeadStatus) {
  return `pill pill--${status}${status === 'new' ? ' pill--new' : ' pill--worked'}`
}

const LEAD_STATUS_HELP: Record<LeadStatus, string> = {
  new: 'ещё не разбирали',
  in_progress: 'связываемся с заказчиком',
  won: 'стал заказом',
  lost: 'не подошёл или отказался',
}

const LEAD_DETAIL_WIDTH_KEY = 'sensor.leads.detailWidth'
const LEAD_LIST_WIDTH_KEY = 'sensor.leads.listWidth'
const LEAD_DETAIL_WIDTH_DEFAULT = 420
const LEAD_LIST_WIDTH_MIN = 360
const LEAD_DETAIL_WIDTH_MIN = 340
const LEAD_DETAIL_WIDTH_MAX = 900
const LEAD_COLUMN_WIDTHS_KEY = 'sensor.leads.columnWidths.v3'

type LeadColumnKey = 'date' | 'message' | 'source' | 'project' | 'score' | 'feedback' | 'status' | 'link'

const DEFAULT_LEAD_COLUMN_WIDTHS: Record<LeadColumnKey, number> = {
  date: 96,
  message: 520,
  source: 210,
  project: 190,
  score: 128,
  feedback: 96,
  status: 148,
  link: 44,
}

const MIN_LEAD_COLUMN_WIDTHS: Record<LeadColumnKey, number> = {
  date: 82,
  message: 280,
  source: 120,
  project: 120,
  score: 104,
  feedback: 72,
  status: 126,
  link: 44,
}

type LeadSplitStyle = CSSProperties & {
  '--lead-list-width'?: string
  '--lead-detail-width'?: string
}

type LeadListCache<T extends LeadResponse = LeadResponse> = {
  items: T[]
  total: number
  page: number
  page_size: number
}

function mergeLeadUpdate<T extends LeadResponse>(item: T, updated: LeadResponse): T {
  return {
    ...item,
    ...updated,
    related_messages:
      updated.related_messages?.length > 0
        ? updated.related_messages
        : item.related_messages,
  } as T
}

function patchLeadListCache<T extends LeadResponse>(
  old: LeadListCache<T> | undefined,
  updated: LeadResponse,
): LeadListCache<T> | undefined {
  if (!old) return old
  return {
    ...old,
    items: old.items.map((item) => (item.id === updated.id ? mergeLeadUpdate(item, updated) : item)),
  }
}

function clampDetailWidth(width: number): number {
  return Math.max(LEAD_DETAIL_WIDTH_MIN, Math.min(LEAD_DETAIL_WIDTH_MAX, Math.round(width)))
}

function useLeadSplitResize() {
  const inboxRef = useRef<HTMLDivElement>(null)
  const [listWidth, setListWidth] = useState<number | null>(() => {
    if (typeof window === 'undefined') return null
    const stored = Number(window.localStorage.getItem(LEAD_LIST_WIDTH_KEY))
    return Number.isFinite(stored) && stored > 0
      ? Math.max(LEAD_LIST_WIDTH_MIN, Math.round(stored))
      : null
  })
  const [detailWidth, setDetailWidth] = useState(() => {
    if (typeof window === 'undefined') return LEAD_DETAIL_WIDTH_DEFAULT
    const stored = Number(window.localStorage.getItem(LEAD_DETAIL_WIDTH_KEY))
    return Number.isFinite(stored) && stored > 0
      ? clampDetailWidth(stored)
      : LEAD_DETAIL_WIDTH_DEFAULT
  })

  useEffect(() => {
    window.localStorage.setItem(LEAD_DETAIL_WIDTH_KEY, String(detailWidth))
  }, [detailWidth])
  useEffect(() => {
    if (listWidth != null) {
      window.localStorage.setItem(LEAD_LIST_WIDTH_KEY, String(listWidth))
    }
  }, [listWidth])

  function startResize(e: ReactPointerEvent<HTMLDivElement>) {
    const box = inboxRef.current?.getBoundingClientRect()
    if (!box) return
    e.preventDefault()
    e.currentTarget.setPointerCapture?.(e.pointerId)

    const resizeTo = (clientX: number) => {
      setListWidth(Math.max(LEAD_LIST_WIDTH_MIN, Math.round(clientX - box.left)))
      setDetailWidth(clampDetailWidth(box.right - clientX - 8))
    }
    const onMove = (event: PointerEvent) => resizeTo(event.clientX)
    const onUp = () => {
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', onUp)
    }

    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointerup', onUp)
  }

  const splitStyle: LeadSplitStyle = {
    ...(listWidth != null ? { '--lead-list-width': `${listWidth}px` } : {}),
    '--lead-detail-width': `${detailWidth}px`,
  }

  return { inboxRef, detailWidth, splitStyle, startResize }
}

function useLeadColumnResize() {
  const [widths, setWidths] = useState<Record<LeadColumnKey, number>>(() => {
    if (typeof window === 'undefined') return DEFAULT_LEAD_COLUMN_WIDTHS
    try {
      const parsed = JSON.parse(window.localStorage.getItem(LEAD_COLUMN_WIDTHS_KEY) || '{}')
      return { ...DEFAULT_LEAD_COLUMN_WIDTHS, ...parsed }
    } catch {
      return DEFAULT_LEAD_COLUMN_WIDTHS
    }
  })

  useEffect(() => {
    window.localStorage.setItem(LEAD_COLUMN_WIDTHS_KEY, JSON.stringify(widths))
  }, [widths])

  function startColumnResize(key: LeadColumnKey, e: ReactPointerEvent<HTMLSpanElement>) {
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget.setPointerCapture?.(e.pointerId)
    const startX = e.clientX
    const startWidth = widths[key]
    const onMove = (event: PointerEvent) => {
      const next = Math.max(MIN_LEAD_COLUMN_WIDTHS[key], Math.round(startWidth + event.clientX - startX))
      setWidths((current) => ({ ...current, [key]: next }))
    }
    const onUp = () => {
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', onUp)
    }
    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointerup', onUp)
  }

  return { widths, startColumnResize }
}

function telegramUserUrl(lead: LeadResponse): string | null {
  if (lead.sender_username) {
    return `https://t.me/${lead.sender_username.replace(/^@/, '')}`
  }
  if (lead.sender_user_id) {
    return `tg://user?id=${lead.sender_user_id}`
  }
  return null
}

function leadAuthorLabel(lead: LeadResponse): string | null {
  if (lead.sender_display_name) return lead.sender_display_name
  if (lead.sender_username) return `@${lead.sender_username.replace(/^@/, '')}`
  if (lead.sender_user_id) return `ID ${lead.sender_user_id}`
  return null
}

function isInternalSourceId(value: string | null | undefined): boolean {
  return !!value && /^-?\d+$/.test(value.trim())
}

function isSensorSource(source: { source_origin?: string | null }): boolean {
  return source.source_origin === 'sensor_managed'
}

function sourceLabel(source: {
  channel_username?: string | null
  source_origin?: string | null
  source_title?: string | null
}): string {
  if (isSensorSource(source)) return 'Источник SENSOR'
  if (source.source_title) return source.source_title
  if (!source.channel_username) return 'Источник'
  if (isInternalSourceId(source.channel_username)) return 'Источник'
  return source.channel_username.startsWith('@')
    ? source.channel_username
    : `@${source.channel_username}`
}

function sourceInitialsForLead(lead: LeadResponse): string {
  if (isSensorSource(lead)) return 'SN'
  const label = sourceLabel(lead)
  const letters = label
    .split(/\s+/)
    .map((word) => Array.from(word).find((char) => /[\p{L}\p{N}]/u.test(char)))
    .filter(Boolean)
  if (letters.length >= 2) return `${letters[0]}${letters[1]}`.toUpperCase()
  return Array.from(label)
    .filter((char) => /[\p{L}\p{N}]/u.test(char))
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'TG'
}

function leadTelegramTarget(lead: LeadResponse): string | null {
  if (isSensorSource(lead)) return telegramUserUrl(lead)
  return lead.url
}

// Keep these thresholds in sync with src/leads/lead_scoring.py.
function confidenceOf(score: number | null | undefined): Confidence {
  if (score == null) return 'na'
  if (score >= 1.15) return 'hi'
  if (score >= 0.95) return 'mid'
  return 'low'
}

// Single definition of a "hot" lead, mirroring is_hot_lead() in the backend:
// high-confidence decision score, not marked "Не лид", still in the "Новый" stage.
function isHotLead(lead: {
  decision_score?: number | null
  feedback_state?: string | null
  lead_status?: string | null
}): boolean {
  return (
    confidenceOf(lead.decision_score) === 'hi' &&
    lead.feedback_state !== 'dislike' &&
    (lead.lead_status ?? 'new') === 'new'
  )
}

function ConfDot({ score }: { score: number | null | undefined }) {
  const conf = confidenceOf(score)
  if (conf === 'na') {
    return <span style={{ color: 'var(--muted-2)', fontFamily: 'var(--font-mono)' }}>—</span>
  }
  return (
    <div className="conf">
      <span className={`dot dot--${conf}`} />
      <span className="num">{(score as number).toFixed(2)}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Lead date (Telegram message time)
// ---------------------------------------------------------------------------

function leadDateParts(iso: string, timeZone?: string): { date: string; time: string } {
  const d = new Date(iso)
  return {
    date: d.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone,
    }),
    time: d.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    }),
  }
}

function leadDateInline(iso: string, timeZone?: string): string {
  const d = new Date(iso)
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  })
}

function leadRelatedMessages(lead: LeadResponse): LeadResponse['related_messages'] {
  return Array.isArray(lead.related_messages) ? lead.related_messages : []
}

function RelatedMessagesInline({ lead }: { lead: LeadResponse }) {
  const related = leadRelatedMessages(lead)
  if (related.length <= 1) return null

  return (
    <div className="lead-related-inline" onClick={(e) => e.stopPropagation()}>
      <span>{related.length} сообщений автора</span>
      {related.slice(0, 6).map((message, index) =>
        isSensorSource(message) ? (
          <span key={message.id} title={message.text}>{index + 1}</span>
        ) : (
          <a
            key={message.id}
            href={message.url}
            target="_blank"
            rel="noopener noreferrer"
            title={message.text}
          >
            {index + 1}
          </a>
        ),
      )}
      {related.length > 6 && <span>+{related.length - 6}</span>}
    </div>
  )
}

function RelatedMessagesPanel({
  lead,
  userTimezone,
}: {
  lead: LeadResponse
  userTimezone: string
}) {
  const related = leadRelatedMessages(lead)
  if (related.length <= 1) return null

  return (
    <div className="detail__related">
      <div className="detail__related__head">
        <span>Сообщения этого автора</span>
        <b>{related.length}</b>
      </div>
      <div className="detail__related__list">
        {related.map((message, index) => {
          const content = (
            <>
            <span className="detail__related__num">{index + 1}</span>
            <span className="detail__related__body">
              <b>
                {sourceLabel(message)} ·{' '}
                {leadDateInline(message.created_at, userTimezone)}
              </b>
              <span>{message.text}</span>
            </span>
            </>
          )
          return isSensorSource(message) ? (
            <span
              key={message.id}
              className={message.id === lead.id ? 'is-current' : undefined}
            >
              {content}
            </span>
          ) : (
            <a
              key={message.id}
              href={message.url}
              target="_blank"
              rel="noopener noreferrer"
              className={message.id === lead.id ? 'is-current' : undefined}
            >
              {content}
            </a>
          )
        })}
      </div>
    </div>
  )
}

function formatUserDateTime(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone,
  })
}

// ---------------------------------------------------------------------------
// Feedback emoji for table cell
// ---------------------------------------------------------------------------

function FeedbackCell({ state }: { state: string | null }) {
  if (!state) return <td className="l-fb l-fb--none">—</td>
  const emoji = state === 'like' ? '👍' : state === 'dislike' ? '👎' : '🤔'
  return <td className="l-fb">{emoji}</td>
}

function LeadProjectBadge({ name, userbotLabel }: { name: string; userbotLabel?: string | null }) {
  return (
    <span
      className="l-project-badge"
      title={userbotLabel ? `Проект: ${name}; юзербот: ${userbotLabel}` : `Проект: ${name}`}
    >
      <span>Проект</span>
      <b>{name}</b>
      {userbotLabel && (
        <>
          <i />
          <span>Юзербот</span>
          <b>{userbotLabel}</b>
        </>
      )}
    </span>
  )
}

function ResizableTh({
  children,
  column,
  onResizeStart,
}: {
  children?: ReactNode
  column: LeadColumnKey
  onResizeStart: (key: LeadColumnKey, e: ReactPointerEvent<HTMLSpanElement>) => void
}) {
  return (
    <th className="leads-table__resizable">
      {children}
      <span
        className="lead-col-resizer"
        role="separator"
        aria-orientation="vertical"
        title="Потяните, чтобы изменить ширину колонки"
        onPointerDown={(e) => onResizeStart(column, e)}
      />
    </th>
  )
}

function LeadColGroup({
  widths,
  includeProject,
}: {
  widths: Record<LeadColumnKey, number>
  includeProject: boolean
}) {
  return (
    <colgroup>
      <col style={{ width: widths.date }} />
      <col style={{ width: widths.message }} />
      <col style={{ width: widths.source }} />
      {includeProject && <col style={{ width: widths.project }} />}
      <col style={{ width: widths.score }} />
      <col style={{ width: widths.feedback }} />
      <col style={{ width: widths.status }} />
      <col style={{ width: widths.link }} />
    </colgroup>
  )
}

function ProjectMultiSelect({
  projects,
  selectedIds,
  onChange,
}: {
  projects: ProjectResponse[] | undefined
  selectedIds: number[]
  onChange: (ids: number[]) => void
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  const selectedNames = useMemo(() => {
    const numbers = buildProjectNumberMap(projects)
    return selectedIds.map((id) => projectLabelById(id, projects, numbers))
  }, [projects, selectedIds])
  const label =
    selectedIds.length === 0
      ? 'Все проекты'
      : selectedIds.length === 1
        ? selectedNames[0]
        : `Выбрано: ${selectedIds.length}`

  const toggle = (id: number) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((item) => item !== id) : [...selectedIds, id])
  }

  return (
    <div className="project-filter" ref={rootRef}>
      <button
        key={selectedIds.join(',') || 'all'}
        type="button"
        className={`project-filter__button${open ? ' is-open' : ''}`}
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{label}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="project-filter__menu" role="listbox" aria-multiselectable="true">
          <button
            type="button"
            className={`project-filter__option${selectedIds.length === 0 ? ' is-selected' : ''}`}
            onClick={() => onChange([])}
          >
            <span className="project-filter__check">
              {selectedIds.length === 0 && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                  <path d="M5 12l4 4 10-10" />
                </svg>
              )}
            </span>
            Все проекты
          </button>
          {(projects ?? []).map((project) => {
            const selected = selectedIds.includes(project.id)
            return (
              <button
                key={project.id}
                type="button"
                className={`project-filter__option${selected ? ' is-selected' : ''}`}
                onClick={() => toggle(project.id)}
              >
                <span className="project-filter__check">
                  {selected && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                      <path d="M5 12l4 4 10-10" />
                    </svg>
                  )}
                </span>
                <span>{projectLabel(project, buildProjectNumberMap(projects))}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Detail side panel (per-project only)
// ---------------------------------------------------------------------------

function DetailPanel({
  lead,
  projectId,
  onClose,
  onPrev,
  onNext,
  onUpdated,
}: {
  lead: LeadResponse
  projectId: number
  onClose: () => void
  onPrev: () => void
  onNext: () => void
  onUpdated: (updated: LeadResponse) => void
}) {
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.getMe(),
  })
  const userTimezone = me?.timezone ?? 'UTC'
  const feedbackMutation = useMutation({
    mutationFn: (fb: 'like' | 'dislike' | 'unsure') =>
      api.setLeadFeedback(projectId, lead.id, fb),
    onSuccess: onUpdated,
  })
  const statusMutation = useMutation({
    mutationFn: (status: LeadStatus) => api.setLeadStatus(projectId, lead.id, status),
    onSuccess: onUpdated,
  })

  const conf = confidenceOf(lead.decision_score ?? lead.base_score)
  const confLabel = conf === 'hi' ? 'высокий' : conf === 'mid' ? 'средний' : 'низкий'
  const isHot = isHotLead(lead)
  const statusClass = `detail__status detail__status--${lead.lead_status}`
  const authorLabel = leadAuthorLabel(lead)
  const authorUrl = telegramUserUrl(lead)
  const telegramTarget = leadTelegramTarget(lead)

  return (
    <aside className="detail" aria-label="Детали лида">
      <button className="detail__back-mobile" onClick={onClose} aria-label="К списку">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        К списку
      </button>
      <div className="detail__head">
        <span className="pill">
          <span className="dot" />
          {isHot ? 'Горячий лид' : `Лид · ${confLabel}`}
        </span>
        <div className="actions">
          <button className="detail__close" onClick={onPrev} aria-label="Предыдущий">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M18 15l-6-6-6 6" />
            </svg>
          </button>
          <button className="detail__close" onClick={onNext} aria-label="Следующий">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <button className="detail__close" onClick={onClose} aria-label="Закрыть">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M6 6l12 12M18 6l-12 12" />
            </svg>
          </button>
        </div>
      </div>

      <div className="detail__body">
        <div className="detail__src">
          <div className="src-avatar">
            {sourceInitialsForLead(lead)}
          </div>
          <div className="src-meta">
            <b>{sourceLabel(lead)}</b>
            <span>
              {leadDateInline(lead.created_at, userTimezone)}
            </span>
            {authorLabel && (
              <span className="src-meta__author">
                Автор:{' '}
                {authorUrl ? (
                  <a href={authorUrl} target="_blank" rel="noopener noreferrer">
                    {authorLabel}
                  </a>
                ) : (
                  <b>{authorLabel}</b>
                )}
              </span>
            )}
          </div>
        </div>

        <p className="detail__msg">{lead.text}</p>

        <RelatedMessagesPanel lead={lead} userTimezone={userTimezone} />

        {lead.ai_reason && (
          <div className="detail__reason">
            <div className="detail__reason__label">Причина AI</div>
            <p>{lead.ai_reason}</p>
          </div>
        )}

        <div className="detail__scores">
          <div>
            <div className="lbl">Decision score</div>
            <div className="val">
              {lead.decision_score != null ? lead.decision_score.toFixed(2) : '—'}
            </div>
          </div>
          <div>
            <div className="lbl">Feedback weight</div>
            <div className="val">
              {lead.feedback_score != null ? lead.feedback_score.toFixed(2) : '—'}
            </div>
          </div>
        </div>

        <div className={`detail__conf is-${conf}`}>
          <div className="bars" aria-hidden="true">
            <i /><i /><i /><i /><i />
          </div>
          <div>
            <div className="lbl">Уверенность</div>
            <div className="lvl">{confLabel}</div>
          </div>
        </div>

        <div className="detail__fb-row">
          <div className="detail__fb-row__label">Ваш фидбэк</div>
          <div className="detail__fb-row__btns">
            {(['like', 'dislike', 'unsure'] as const).map((fb) => {
              const cls = `fb-btn fb-btn--${fb === 'like' ? 'good' : fb === 'dislike' ? 'bad' : 'unsure'}${lead.feedback_state === fb ? ' is-active' : ''}`
              const emoji = fb === 'like' ? '👍' : fb === 'dislike' ? '👎' : '🤔'
              const label = fb === 'like' ? 'Лид' : fb === 'dislike' ? 'Не лид' : 'Не уверен'
              return (
                <button
                  key={fb}
                  className={cls}
                  onClick={() => feedbackMutation.mutate(fb)}
                  disabled={feedbackMutation.isPending}
                >
                  <span className="emoji">{emoji}</span> {label}
                </button>
              )
            })}
          </div>
        </div>

        {lead.status_changed_at && lead.lead_status !== 'new' && (
          <div className={statusClass}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              style={{ width: 13, height: 13 }}
            >
              <path d="M5 12l4 4 10-10" />
            </svg>
            <span className={`status-label status-label--${lead.lead_status}`}>
              <LeadStatusText status={lead.lead_status} />
            </span>
            <b>{formatUserDateTime(lead.status_changed_at, userTimezone)}</b>
          </div>
        )}

        <div className="detail__fb-row">
          <div className="detail__fb-row__label">Статус воронки</div>
          <div className="detail__fb-row__btns">
            {(Object.keys(LEAD_STATUS_LABELS) as LeadStatus[]).map((status) => (
              <button
                key={status}
                className={`fb-btn status-btn status-btn--${status}${lead.lead_status === status ? ' is-active' : ''}`}
                onClick={() => statusMutation.mutate(status)}
                disabled={statusMutation.isPending}
                title={LEAD_STATUS_HELP[status]}
              >
                <span className={`status-label status-label--${status}`}>
                  <LeadStatusText status={status} />
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="detail__foot">
          {telegramTarget && (
            <a
              href={telegramTarget}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn--primary"
            >
              {isSensorSource(lead) ? 'Открыть автора' : 'Открыть в Telegram'}
              <span className="arrow">↗</span>
            </a>
          )}
        </div>
      </div>
    </aside>
  )
}

// ---------------------------------------------------------------------------
// Shared filter state
// ---------------------------------------------------------------------------

interface Filters {
  feedback_states: FeedbackFilter[]
  hot_only: boolean
  search: string
}

type FeedbackFilter = 'like' | 'dislike' | 'unsure' | 'none'
const FEEDBACK_FILTERS: FeedbackFilter[] = ['like', 'dislike', 'unsure', 'none']
const DEFAULT_FEEDBACK_FILTERS: FeedbackFilter[] = ['like', 'unsure', 'none']
const FEEDBACK_FILTER_META: Record<FeedbackFilter, { emoji: string; label: string }> = {
  like: { emoji: '👍', label: 'Лиды' },
  dislike: { emoji: '👎', label: 'Не лиды' },
  unsure: { emoji: '🤔', label: 'Не уверен' },
  none: { emoji: '○', label: 'Без оценки' },
}

function buildFeedbackQuery(states: FeedbackFilter[]): string[] | undefined {
  if (states.length === FEEDBACK_FILTERS.length) return undefined
  return states
}

function initialFeedbackFilters(searchParams?: URLSearchParams): FeedbackFilter[] {
  return searchParams?.get('feedback') === 'none' ? ['none'] : [...DEFAULT_FEEDBACK_FILTERS]
}

function sameFeedbackSet(left: FeedbackFilter[], right: FeedbackFilter[]): boolean {
  return left.length === right.length && left.every((item) => right.includes(item))
}

function toggleFeedbackState(states: FeedbackFilter[], state: FeedbackFilter): FeedbackFilter[] {
  if (states.includes(state)) {
    if (states.length === 1) return states
    return states.filter((item) => item !== state)
  }
  return [...states, state]
}

function feedbackFilterLabel(states: FeedbackFilter[]): string {
  if (states.length === FEEDBACK_FILTERS.length) return 'Оценка: Все'
  if (states.length === 1) return `Оценка: ${FEEDBACK_FILTER_META[states[0]].label}`
  return `Оценка: ${states.length}`
}

function FeedbackStateSelect({
  selected,
  onChange,
}: {
  selected: FeedbackFilter[]
  onChange: (states: FeedbackFilter[]) => void
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  return (
    <div className="feedback-select" ref={rootRef}>
      <button
        key={selected.join(',')}
        type="button"
        className={`feedback-select__button${open ? ' is-open' : ''}`}
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{feedbackFilterLabel(selected)}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="feedback-select__menu" role="listbox" aria-multiselectable="true">
          <div className="feedback-select__title">Точно выбрать</div>
          {FEEDBACK_FILTERS.map((state) => {
            const { emoji, label } = FEEDBACK_FILTER_META[state]
            const active = selected.includes(state)
            return (
              <button
                key={state}
                type="button"
                className={`feedback-select__option${active ? ' is-selected' : ''}`}
                onClick={() => onChange(toggleFeedbackState(selected, state))}
              >
                <span className="feedback-select__check">
                  {active && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                      <path d="M5 12l4 4 10-10" />
                    </svg>
                  )}
                </span>
                <span className="feedback-select__emoji">{emoji}</span>
                <span>{label}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Org-wide inbox view (no ?project=N)
// ---------------------------------------------------------------------------

function OrgLeadsInbox() {
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const pageSize = 50
  const [filters, setFilters] = useState<Filters>({
    feedback_states: initialFeedbackFilters(searchParams),
    hot_only: searchParams.get('hot') === '1',
    search: '',
  })
  // Project filter within the org inbox
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [leadOverrides, setLeadOverrides] = useState<Record<number, LeadResponse>>({})
  const { inboxRef, detailWidth, splitStyle, startResize } = useLeadSplitResize()
  const { widths: columnWidths, startColumnResize } = useLeadColumnResize()
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.getMe(),
  })
  const userTimezone = me?.timezone ?? 'UTC'

  // Fetch org projects for the filter dropdown
  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.getProjects(),
  })

  // Fetch all org leads
  const { data, isLoading } = useQuery({
    queryKey: ['org-leads', page, filters, selectedProjectIds],
    queryFn: () =>
      api.getAllLeads({
        page,
        page_size: pageSize,
        project_ids: selectedProjectIds.length ? selectedProjectIds : undefined,
        feedback: buildFeedbackQuery(filters.feedback_states),
        hot: filters.hot_only || undefined,
        q: filters.search || undefined,
      }),
  })

  const activeFilterCount =
    (filters.hot_only ? 1 : 0) +
    (!sameFeedbackSet(filters.feedback_states, DEFAULT_FEEDBACK_FILTERS) ? 1 : 0) +
    (filters.search ? 1 : 0) +
    (selectedProjectIds.length > 0 ? 1 : 0)

  const totalLabel = data?.total != null ? `${data.total} лидов во всех проектах` : 'Лиды во всех проектах'
  const items = useMemo(
    () =>
      data?.items.map((item) =>
        leadOverrides[item.id] ? mergeLeadUpdate(item, leadOverrides[item.id]) : item,
      ) ?? [],
    [data, leadOverrides],
  )
  const selected = useMemo(
    () => (selectedId == null ? null : items.find((l) => l.id === selectedId) ?? null),
    [selectedId, items],
  )

  useEffect(() => {
    if (selectedId != null && data?.items && !items.some((l) => l.id === selectedId)) {
      setSelectedId(null)
    }
  }, [selectedId, data, items])

  const goPrev = () => {
    if (!items.length || selectedId == null) return
    const idx = items.findIndex((l) => l.id === selectedId)
    if (idx > 0) setSelectedId(items[idx - 1].id)
  }
  const goNext = () => {
    if (!items.length || selectedId == null) return
    const idx = items.findIndex((l) => l.id === selectedId)
    if (idx >= 0 && idx < items.length - 1) setSelectedId(items[idx + 1].id)
  }

  const updateLeadInCache = (updated: LeadResponse) => {
    setLeadOverrides((current) => ({ ...current, [updated.id]: updated }))
    queryClient.setQueriesData<LeadListCache<OrgLeadResponse>>(
      { queryKey: ['org-leads'] },
      (old) => patchLeadListCache(old, updated),
    )
    queryClient.setQueriesData<LeadListCache<LeadResponse>>(
      { queryKey: ['leads', updated.lead_project_id] },
      (old) => patchLeadListCache(old, updated),
    )
  }

  return (
    <section className="ds-view is-active" data-view="leads" data-screen-label="Лиды">
      <div className="ds-pagehead">
        <div>
          <span className="eyebrow">{totalLabel}</span>
          <h1>Лиды</h1>
          <p>Релевантные сообщения из ваших источников. Отметьте 👍/👎 — SENSOR станет точнее.</p>
        </div>
      </div>

      <div className="state-default-only">
        <div className="filters">
          <ProjectMultiSelect
            projects={projects}
            selectedIds={selectedProjectIds}
            onChange={(ids) => {
              setSelectedProjectIds(ids)
              setPage(1)
            }}
          />

          <div className="filters__divider" />

          {/* Search */}
          <div className="filters__search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4.3-4.3" />
            </svg>
            <input
              type="search"
              placeholder="Поиск по тексту, причине AI…"
              value={filters.search}
              onChange={(e) => {
                setFilters({ ...filters, search: e.target.value })
                setPage(1)
              }}
            />
          </div>

          <button
            type="button"
            className={`chip-filter${filters.hot_only ? ' is-active is-on-teal' : ''}`}
            onClick={() => {
              setFilters({ ...filters, hot_only: !filters.hot_only })
              setPage(1)
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M13 2l-1 8h6l-9 12 1-8H4z" fill="currentColor" />
            </svg>
            Только горячие
            {filters.hot_only && (
              <span className="chip-x" aria-label="Снять">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                  <path d="M6 6l12 12M18 6l-12 12" />
                </svg>
              </span>
            )}
          </button>

          <div className="filters__divider" />

          <FeedbackStateSelect
            selected={filters.feedback_states}
            onChange={(states) => {
              setFilters({ ...filters, feedback_states: states })
              setPage(1)
            }}
          />

          {activeFilterCount > 0 && (
            <button
              type="button"
              className="filters__reset"
              onClick={() => {
                setFilters({ feedback_states: [...DEFAULT_FEEDBACK_FILTERS], hot_only: false, search: '' })
                setSelectedProjectIds([])
                setPage(1)
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M6 6l12 12M18 6l-12 12" />
              </svg>
              Сбросить{' '}
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, opacity: 0.75, marginLeft: 2 }}>
                ({activeFilterCount})
              </span>
            </button>
          )}
        </div>

        {/* Org-wide leads table */}
        <div
          ref={inboxRef}
          className={`inbox${selected ? ' has-panel' : ''}`}
          style={selected ? splitStyle : undefined}
        >
          <div className="leads-wrap">
            <table className="leads-table">
              <LeadColGroup widths={columnWidths} includeProject={false} />
              <thead>
                <tr>
                  <ResizableTh column="date" onResizeStart={startColumnResize}>Дата</ResizableTh>
                  <ResizableTh column="message" onResizeStart={startColumnResize}>Сообщение / причина AI</ResizableTh>
                  <ResizableTh column="source" onResizeStart={startColumnResize}>Источник</ResizableTh>
                  <ResizableTh column="score" onResizeStart={startColumnResize}>Score</ResizableTh>
                  <ResizableTh column="feedback" onResizeStart={startColumnResize}>Фидбэк</ResizableTh>
                  <ResizableTh column="status" onResizeStart={startColumnResize}>Статус</ResizableTh>
                  <th />
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '36px 0', color: 'var(--muted)' }}>
                      Загрузка…
                    </td>
                  </tr>
                )}
                {items.map((lead: OrgLeadResponse) => {
                  const dt = leadDateParts(lead.created_at, userTimezone)
                  const isActive = lead.id === selectedId
                  const telegramTarget = leadTelegramTarget(lead)
                  return (
                    <tr
                      key={lead.id}
                      className={isActive ? 'is-active' : undefined}
                      onClick={() => setSelectedId(lead.id)}
                    >
                      <td className="l-date">
                        {dt.date}
                        <small>{dt.time}</small>
                      </td>
                      <td className="l-text">
                        <LeadProjectBadge
                          name={projectLabelById(
                            lead.lead_project_id,
                            projects,
                            buildProjectNumberMap(projects),
                          )}
                          userbotLabel={lead.project_userbot_label}
                        />
                        <div className="l-text__msg">{lead.text}</div>
                        {lead.ai_reason && (
                          <div className="l-text__reason">
                            <b>Причина:</b> {lead.ai_reason}
                          </div>
                        )}
                        <RelatedMessagesInline lead={lead} />
                      </td>
                      <td className="l-source">
                        <span className="l-source__name">{sourceLabel(lead)}</span>
                        {lead.scan_id == null && (
                          <span className="src-pill src-pill--live">live</span>
                        )}
                        {lead.scan_id != null && (
                          <span className="src-pill src-pill--hist">hist</span>
                        )}
                      </td>
                      <td className="l-score">
                        <ConfDot score={lead.decision_score ?? lead.base_score} />
                        {lead.feedback_score != null && (
                          <div className="fb">
                            feedback {lead.feedback_score >= 0 ? '+' : ''}
                            {lead.feedback_score.toFixed(2)}
                          </div>
                        )}
                      </td>
                      <FeedbackCell state={lead.feedback_state} />
                      <td className="l-state">
                        <span className={leadStatusPillClass(lead.lead_status)}>
                          <LeadStatusText status={lead.lead_status} />
                        </span>
                      </td>
                      <td className="l-link-cell" onClick={(e) => e.stopPropagation()}>
                        {telegramTarget && (
                          <a
                            className="l-link"
                            href={telegramTarget}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={isSensorSource(lead) ? 'Открыть автора' : 'Открыть в Telegram'}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                              <path d="M7 17L17 7M9 7h8v8" />
                            </svg>
                          </a>
                        )}
                      </td>
                    </tr>
                  )
                })}
                {data && items.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '36px 0', color: 'var(--muted)' }}>
                      {activeFilterCount > 0
                        ? 'Лидов не найдено — попробуйте изменить фильтры.'
                        : 'Лидов пока нет. Создайте проект и добавьте источники через бота.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {data && data.total > pageSize && (
              <div className="table-foot">
                <span className="info">
                  Показано {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, data.total)} из{' '}
                  {data.total}
                </span>
                <div className="pager">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    aria-label="Назад"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path d="M15 6l-6 6 6 6" />
                    </svg>
                  </button>
                  <button className="is-active">{page}</button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page * pageSize >= data.total}
                    aria-label="Вперёд"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          {selected && (
            <div
              className="lead-split-resizer"
              role="separator"
              aria-orientation="vertical"
              aria-valuemin={LEAD_DETAIL_WIDTH_MIN}
              aria-valuemax={LEAD_DETAIL_WIDTH_MAX}
              aria-valuenow={detailWidth}
              title="Потяните, чтобы изменить ширину панели"
              onPointerDown={startResize}
            />
          )}

          {selected && (
            <DetailPanel
              key={selected.id}
              lead={selected}
              projectId={selected.lead_project_id}
              onClose={() => setSelectedId(null)}
              onPrev={goPrev}
              onNext={goNext}
              onUpdated={(updated) => {
                updateLeadInCache(updated)
                void queryClient.invalidateQueries({ queryKey: ['org-leads'] })
              }}
            />
          )}
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Per-project view (unchanged behavior)
// ---------------------------------------------------------------------------

function ProjectLeadsView({ projectId, source }: { projectId: number; source?: string }) {
  const [page, setPage] = useState(1)
  const pageSize = 50
  const [filters, setFilters] = useState<Filters>({
    feedback_states: [...DEFAULT_FEEDBACK_FILTERS],
    hot_only: false,
    search: '',
  })
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [leadOverrides, setLeadOverrides] = useState<Record<number, LeadResponse>>({})
  const { inboxRef, detailWidth, splitStyle, startResize } = useLeadSplitResize()
  const { widths: columnWidths, startColumnResize } = useLeadColumnResize()
  const queryClient = useQueryClient()
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.getMe(),
  })
  const userTimezone = me?.timezone ?? 'UTC'
  const aiAvailable = !!me?.has_ai

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId),
  })
  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.getProjects(),
  })

  const { data, isLoading } = useQuery({
    queryKey: ['leads', projectId, source, page, filters],
    queryFn: () =>
      api.getLeads(projectId, {
        page,
        page_size: pageSize,
        hot_only: filters.hot_only || undefined,
        feedback_state: buildFeedbackQuery(filters.feedback_states),
        search: filters.search || undefined,
        source,
      }),
  })

  useEffect(() => {
    setPage(1)
  }, [source])

  const items = useMemo(
    () =>
      data?.items.map((item) =>
        leadOverrides[item.id] ? mergeLeadUpdate(item, leadOverrides[item.id]) : item,
      ) ?? [],
    [data, leadOverrides],
  )
  const selected = useMemo(
    () => (selectedId == null ? null : items.find((l) => l.id === selectedId) ?? null),
    [selectedId, items],
  )

  useEffect(() => {
    if (selectedId != null && data?.items && !items.some((l) => l.id === selectedId)) {
      setSelectedId(null)
    }
  }, [selectedId, data, items])

  const activeFilterCount =
    (filters.hot_only ? 1 : 0) +
    (!sameFeedbackSet(filters.feedback_states, DEFAULT_FEEDBACK_FILTERS) ? 1 : 0) +
    (filters.search ? 1 : 0)
  const projectNumbers = buildProjectNumberMap(projects)
  const projectName = project
    ? projectLabel(project, projectNumbers)
    : projectLabelById(projectId, projects, projectNumbers)
  const totalLabel = data?.total != null ? `${data.total} лидов` : 'Лиды'

  const goPrev = () => {
    if (!items.length || selectedId == null) return
    const idx = items.findIndex((l) => l.id === selectedId)
    if (idx > 0) setSelectedId(items[idx - 1].id)
  }
  const goNext = () => {
    if (!items.length || selectedId == null) return
    const idx = items.findIndex((l) => l.id === selectedId)
    if (idx >= 0 && idx < items.length - 1) setSelectedId(items[idx + 1].id)
  }

  const updateLeadInCache = (updated: LeadResponse) => {
    setLeadOverrides((current) => ({ ...current, [updated.id]: updated }))
    queryClient.setQueriesData<LeadListCache<LeadResponse>>(
      { queryKey: ['leads', projectId] },
      (old) => patchLeadListCache(old, updated),
    )
    queryClient.setQueriesData<LeadListCache<OrgLeadResponse>>(
      { queryKey: ['org-leads'] },
      (old) => patchLeadListCache(old, updated),
    )
  }

  return (
    <section className="ds-view is-active" data-view="leads" data-screen-label="Лиды">
      <div className="ds-pagehead">
        <div>
          <span className="eyebrow">
            {totalLabel}
            {items.some(isHotLead) && ` · горячие в списке`}
          </span>
          <h1>Лиды</h1>
          <p>
            Проект: <b>{projectName}</b>. Релевантные сообщения из ваших источников.
            Отметьте 👍/👎 — SENSOR станет точнее.
          </p>
        </div>
        <div className="ds-pagehead__actions">
          <button
            className="btn btn--ghost btn--sm"
            onClick={async () => {
              const blob = await api.exportLeads(projectId)
              const url = URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = `leads-${projectId}.csv`
              document.body.appendChild(a)
              a.click()
              a.remove()
              URL.revokeObjectURL(url)
            }}
          >
            Экспорт CSV
          </button>
        </div>
      </div>

      {aiAvailable ? (
        <div style={{ margin: '4px 0 18px' }}>
          <HistoryScanControl projectId={projectId} aiAvailable={aiAvailable} />
        </div>
      ) : null}

      <div className="state-default-only">
        <div className="filters">
          <div className="filters__search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4.3-4.3" />
            </svg>
            <input
              type="search"
              placeholder="Поиск по тексту, причине AI, источнику…"
              value={filters.search}
              onChange={(e) => {
                setFilters({ ...filters, search: e.target.value })
                setPage(1)
              }}
            />
          </div>

          <button
            type="button"
            className={`chip-filter${filters.hot_only ? ' is-active is-on-teal' : ''}`}
            onClick={() => {
              setFilters({ ...filters, hot_only: !filters.hot_only })
              setPage(1)
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M13 2l-1 8h6l-9 12 1-8H4z" fill="currentColor" />
            </svg>
            Только горячие
            {filters.hot_only && (
              <span className="chip-x" aria-label="Снять">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                  <path d="M6 6l12 12M18 6l-12 12" />
                </svg>
              </span>
            )}
          </button>

          <div className="filters__divider" />

          <FeedbackStateSelect
            selected={filters.feedback_states}
            onChange={(states) => {
              setFilters({ ...filters, feedback_states: states })
              setPage(1)
            }}
          />

          {activeFilterCount > 0 && (
            <button
              type="button"
              className="filters__reset"
              onClick={() => {
                setFilters({ feedback_states: [...DEFAULT_FEEDBACK_FILTERS], hot_only: false, search: '' })
                setPage(1)
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M6 6l12 12M18 6l-12 12" />
              </svg>
              Сбросить{' '}
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, opacity: 0.75, marginLeft: 2 }}>
                ({activeFilterCount})
              </span>
            </button>
          )}
        </div>

        <div
          ref={inboxRef}
          className={`inbox${selected ? ' has-panel' : ''}`}
          style={selected ? splitStyle : undefined}
        >
          <div className="leads-wrap">
            <table className="leads-table">
              <LeadColGroup widths={columnWidths} includeProject={false} />
              <thead>
                <tr>
                  <ResizableTh column="date" onResizeStart={startColumnResize}>Дата</ResizableTh>
                  <ResizableTh column="message" onResizeStart={startColumnResize}>Сообщение / причина AI</ResizableTh>
                  <ResizableTh column="source" onResizeStart={startColumnResize}>Источник</ResizableTh>
                  <ResizableTh column="score" onResizeStart={startColumnResize}>Score</ResizableTh>
                  <ResizableTh column="feedback" onResizeStart={startColumnResize}>Фидбэк</ResizableTh>
                  <ResizableTh column="status" onResizeStart={startColumnResize}>Статус</ResizableTh>
                  <th />
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '36px 0', color: 'var(--muted)' }}>
                      Загрузка…
                    </td>
                  </tr>
                )}
                {items.map((lead) => {
                  const dt = leadDateParts(lead.created_at, userTimezone)
                  const isActive = lead.id === selectedId
                  const telegramTarget = leadTelegramTarget(lead)
                  return (
                    <tr
                      key={lead.id}
                      className={isActive ? 'is-active' : undefined}
                      onClick={() => setSelectedId(lead.id)}
                    >
                      <td className="l-date">
                        {dt.date}
                        <small>{dt.time}</small>
                      </td>
                      <td className="l-text">
                        <LeadProjectBadge name={projectName} userbotLabel={project?.userbot_label} />
                        <div className="l-text__msg">{lead.text}</div>
                        {lead.ai_reason && (
                          <div className="l-text__reason">
                            <b>Причина:</b> {lead.ai_reason}
                          </div>
                        )}
                        <RelatedMessagesInline lead={lead} />
                      </td>
                      <td className="l-source">
                        <span className="l-source__name">{sourceLabel(lead)}</span>
                        {lead.scan_id == null && (
                          <span className="src-pill src-pill--live">live</span>
                        )}
                        {lead.scan_id != null && (
                          <span className="src-pill src-pill--hist">hist</span>
                        )}
                      </td>
                      <td className="l-score">
                        <ConfDot score={lead.decision_score ?? lead.base_score} />
                        {lead.feedback_score != null && (
                          <div className="fb">
                            feedback {lead.feedback_score >= 0 ? '+' : ''}
                            {lead.feedback_score.toFixed(2)}
                          </div>
                        )}
                      </td>
                      <FeedbackCell state={lead.feedback_state} />
                      <td className="l-state">
                        <span className={leadStatusPillClass(lead.lead_status)}>
                          <LeadStatusText status={lead.lead_status} />
                        </span>
                      </td>
                      <td className="l-link-cell" onClick={(e) => e.stopPropagation()}>
                        {telegramTarget && (
                          <a
                            className="l-link"
                            href={telegramTarget}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={isSensorSource(lead) ? 'Открыть автора' : 'Открыть в Telegram'}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                              <path d="M7 17L17 7M9 7h8v8" />
                            </svg>
                          </a>
                        )}
                      </td>
                    </tr>
                  )
                })}
                {data && items.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '36px 0', color: 'var(--muted)' }}>
                      Лидов не найдено
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {data && data.total > pageSize && (
              <div className="table-foot">
                <span className="info">
                  Показано {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, data.total)} из{' '}
                  {data.total}
                </span>
                <div className="pager">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    aria-label="Назад"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path d="M15 6l-6 6 6 6" />
                    </svg>
                  </button>
                  <button className="is-active">{page}</button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page * pageSize >= data.total}
                    aria-label="Вперёд"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          {selected && (
            <div
              className="lead-split-resizer"
              role="separator"
              aria-orientation="vertical"
              aria-valuemin={LEAD_DETAIL_WIDTH_MIN}
              aria-valuemax={LEAD_DETAIL_WIDTH_MAX}
              aria-valuenow={detailWidth}
              title="Потяните, чтобы изменить ширину панели"
              onPointerDown={startResize}
            />
          )}

          {selected && (
            <DetailPanel
              key={selected.id}
              lead={selected}
              projectId={projectId}
              onClose={() => setSelectedId(null)}
              onPrev={goPrev}
              onNext={goNext}
              onUpdated={(updated) => {
                updateLeadInCache(updated)
                void queryClient.invalidateQueries({ queryKey: ['leads', projectId] })
              }}
            />
          )}
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Page root — routes between org inbox and per-project view
// ---------------------------------------------------------------------------

export function LeadsPage() {
  const [searchParams] = useSearchParams()
  const projectId = Number(searchParams.get('project')) || undefined
  const source = searchParams.get('source') || undefined

  if (!projectId) {
    return <OrgLeadsInbox />
  }

  return <ProjectLeadsView projectId={projectId} source={source} />
}
